import React, {useEffect, useState} from "react";
import { Swiper, SwiperSlide } from 'swiper/react';
import { Thumbs } from 'swiper/modules';
import 'swiper/css';
import {Tab} from "@mui/material";
import Agent from "../../../Network/Agent";
import ProductListEmptyState from "./ProductListEmptyState";
import {ListStoreEnum} from "../Enum/ListStoreEnum";
import {Link, useNavigate, useParams} from "react-router-dom";
import {getGeneratedRoute, RouteEnumFinal} from "../../../Config/RouteEnum";
import ProductCardGrid from "../../Product/ProductCardGrid";
import ProductCardVertical from "../../Product/ProductCardVertical";
import ProductCardNoPic from "../../Product/ProductCardNoPic";
import SpinnerCustom from "../../ui/SpinnerCustom";
import {Spinner} from "react-bootstrap";

import {useInfiniteQuery, useQuery} from "@tanstack/react-query";
import {getStoreName} from "../../Utils/getStoreName";
import {useSelector} from "react-redux";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faPlus} from "@fortawesome/free-solid-svg-icons";

const CategoryProductSlider = ({ primaryColor, listType }) => {
    const [thumbsSwiper, setThumbsSwiper] = useState(null);
    const [activeSlideIndex, setActiveSlideIndex] = useState(0);

    const storeUsername = getStoreName();
    const navigate = useNavigate();
    const params = useParams();
    const activeCategoryCode = params.arg;

    const { isInCustomerMode } = useSelector(state => state.store);

    const { data: categories = [] } = useQuery({
        queryKey: ['categories', storeUsername],
        queryFn: () => Agent.Category.getList(storeUsername)
            .then(res => {
                const {categories} = res.data.result
                if (activeCategoryCode === 'new' && categories.length) {
                    navigate(getGeneratedRoute(RouteEnumFinal.MAIN_URL_GENERATE, categories.at(0).code))
                    setActiveSlideIndex(0)
                }
                return categories
            }),
    });
    const {
        data,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        isFetching,
    } = useInfiniteQuery({
        queryKey: ['products', activeCategoryCode],
        queryFn: ({ pageParam = 1 }) =>
            Agent.Product.getList(activeCategoryCode || 'all', storeUsername, pageParam).then(res => {
                return {
                    products: res.data.result?.products,
                    hasMore: res.data.meta.hasMore, // Pass hasMore from API
                };
            }),
        getNextPageParam: (lastPage, allPages) => {
            // Use hasMore from the last page to determine if more pages are available
            return lastPage.hasMore ? allPages.length + 1 : undefined;
        },
        enabled: !!activeCategoryCode && activeCategoryCode !== 'new',
        staleTime: 5 * 60 * 1000,
    });

    const products = data?.pages.flatMap((page) => page.products) || [];
    const renderProductList = () => {
        if (products.length === 0) {
            return <ProductListEmptyState />;
        }
        switch (listType) {
            case ListStoreEnum.GRID:
                return (
                    <div className="container">
                        <div className="row">
                            {products.map(product => (
                                <div key={product.code} className="col-6">
                                    <Link
                                        to={isInCustomerMode
                                            ? getGeneratedRoute(RouteEnumFinal.PRODUCT_SHOW_URL_GENERATE, product.code)
                                            : getGeneratedRoute(RouteEnumFinal.PRODUCT_SHOW_OWNER_MODE_URL_GENERATE, product.code)}
                                        className="td-none">
                                        <ProductCardGrid
                                            isActive={product.isActive}
                                            isForShow={product.isForShow}
                                            title={product.title}
                                            description={product.brief}
                                            thumbnail={product.thumbnail.internalPath}
                                            dominantColor={product.thumbnail.dominantColor.hex}
                                            price={product.price} />
                                    </Link>
                                </div>
                            ))}
                        </div>
                    </div>
                );
            case ListStoreEnum.VERTICAL:
                return (
                    <div className="container">
                        {products.map(product => (
                            <Link
                                to={isInCustomerMode
                                    ? getGeneratedRoute(RouteEnumFinal.PRODUCT_SHOW_URL_GENERATE, product.code)
                                    : getGeneratedRoute(RouteEnumFinal.PRODUCT_SHOW_OWNER_MODE_URL_GENERATE, product.code)}
                                key={product.code}
                                className="td-none">
                                <ProductCardVertical
                                    isActive={product.isActive}
                                    isForShow={product.isForShow}
                                    title={product.title}
                                    description={product.brief}
                                    thumbnail={product.thumbnail.internalPath}
                                    dominantColor={product.thumbnail.dominantColor.hex}
                                    price={product.price} />
                            </Link>
                        ))}
                    </div>
                );
            case ListStoreEnum.NO_PIC:
                return (
                    <div className="container">
                        {products.map(product => (
                            <Link
                                to={isInCustomerMode
                                    ? getGeneratedRoute(RouteEnumFinal.PRODUCT_SHOW_URL_GENERATE, product.code)
                                    : getGeneratedRoute(RouteEnumFinal.PRODUCT_SHOW_OWNER_MODE_URL_GENERATE, product.code)}
                                key={product.code}
                                className="td-none">
                                <ProductCardNoPic
                                    isActive={product.isActive}
                                    isForShow={product.isForShow}
                                    title={product.title}
                                    description={product.brief}
                                    price={product.price} />
                            </Link>
                        ))}
                    </div>
                );
        }
    };
    const handleTabClick = (index, categoryCode) => {
        setActiveSlideIndex(index);
        navigate(`/${categoryCode}`, { replace: true });
    };
    const handleSwipe = (swiper) => {
        const newIndex = swiper.activeIndex;
        const code = categories[newIndex].code;
        const target = newIndex === 0 ? newIndex : (newIndex - 1);
        thumbsSwiper.slideTo(target);
        setActiveSlideIndex(newIndex);
        navigate(`/${code}`, { replace: true });
    };

    useEffect(() => {
        const index = categories.findIndex(obj => obj.code === activeCategoryCode);
        setActiveSlideIndex(index);
        thumbsSwiper?.slideTo(index);
    }, [categories, thumbsSwiper]);
    useEffect(() => {
        const handleScroll = () => {
            const { scrollTop, scrollHeight, clientHeight } = document.documentElement;
            const threshold = 1000;

            if (scrollHeight - scrollTop <= clientHeight + threshold && hasNextPage && !isFetchingNextPage) {
                fetchNextPage();
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [hasNextPage, isFetchingNextPage]);

    return (
        <>
            <Swiper
                spaceBetween={1}
                slidesPerView={'auto'}
                modules={[Thumbs]}
                onSwiper={setThumbsSwiper}
                watchSlidesProgress={true}
                freeMode={true}
                lazyPreloadPrevNext={1} // Preload images in adjacent slides
                className="tabs-container-1"
            >
                {categories.map((item, index) => (
                    <SwiperSlide
                        className="w-auto cursor-pointer"
                        key={index} onClick={() => handleTabClick(index, item.code)}
                    >
                        <Tab
                            label={item.title}
                            className={`tabs-custom-1 ${activeSlideIndex === index ? 'Mui-selected' : ''}`}
                            style={{ color: primaryColor }} />
                        <div className="underline" style={{
                            backgroundColor: primaryColor,
                            width: activeSlideIndex === index ? '100%' : '0',
                        }}></div>
                    </SwiperSlide>
                ))}
            </Swiper>

            <Swiper
                spaceBetween={8}
                thumbs={{ swiper: thumbsSwiper }}
                modules={[Thumbs]}
                watchSlidesProgress
                onSlideChange={handleSwipe}>
                {categories.map(category => (
                    <SwiperSlide key={category.code}>
                        {isFetching && !isFetchingNextPage ? (
                            <SpinnerCustom className="pt-96">
                                <Spinner
                                    className={'custom-spinner d-inline-block'}
                                    style={{ color: primaryColor, width: '36px', height: '36px' }}
                                    animation="border" />
                            </SpinnerCustom>
                        ) : (
                            renderProductList()
                        )}
                    </SwiperSlide>
                ))}
            </Swiper>

            {isFetchingNextPage && (
                <SpinnerCustom className="pt-20">
                    <Spinner
                        className={'custom-spinner d-inline-block'}
                        style={{ color: primaryColor, width: '36px', height: '36px' }}
                        animation="border" />
                </SpinnerCustom>
            )}

            {
                activeCategoryCode === 'new' &&
                    <div className="mobile-subtitle-regular text-center pt-96 px-4">
                        هیچ دسته بندی ای برای این فروشگاه وجود ندارد.
                    </div>
            }
            {
                (activeCategoryCode === 'new' && !isInCustomerMode) &&
                    <div className="mobile-subtitle-regular text-center mt-12 px-4">
                        برای افزودن دسته بندی دکمه
                        <FontAwesomeIcon className="px-2" icon={faPlus} style={{fontSize: '16px', color: primaryColor}}/>
                        در قسمت پایین صفحه را لمس کنید.
                    </div>
            }
        </>
    );
};

export default CategoryProductSlider